import {djangoFetchJson} from 'common/report/queries/utils/index';
import {apiClient} from 'common/util/api-client';
import {objToFormData} from 'common/util';
import type {RegisterRequest} from 'common/types/schema4api/user/types';

export interface SignupOptions {
  name: string;
  email: string;
  password: string;
  socialCredential: string;
  company: string;
  phone?: string;
  numberOfEmployees?: number;
  distinctId: string;
  extras: UnknownObj;
  isInvited: boolean;
  inviter?: string;
  next?: string;
  promoSlug?: string;
  agreedToEmailPromo?: boolean;
  numberOfMaus?: number;
  department?: string;
  incubator?: string;
  creditStartup: boolean;
  url: string;
  avatar?: Blob;
  sscid?: string;
  guestId?: string;
}

export type SignupSuccessResponse = {
  statusText: `success`;
  redirectUrl: string;
};

export type SignupErrorResponse = {
  statusText: `error`;
  errors: {[field: string]: [{code: string; message: string}]};
};

export type SignupApiResponse = SignupErrorResponse | SignupSuccessResponse;

export const signupUrl = `/register_ajax/`;

export async function signup(options: SignupOptions): Promise<SignupApiResponse> {
  const {
    name,
    email,
    password,
    socialCredential,
    company,
    isInvited,
    distinctId,
    extras,
    url,
    creditStartup,
    agreedToEmailPromo,
    numberOfEmployees,
    phone,
    next,
    promoSlug,
    inviter,
    incubator,
    numberOfMaus,
    department,
    sscid,
  } = options;

  const userData: RegisterRequest = {
    // required fields:
    name,
    email,
    password1: password,
    social_credential: socialCredential,
    company,
    invited: isInvited ? 1 : 0, // invited as defined as integer field in user/forms.py so we need number instead of boolean
    distinct_id: distinctId,
    signup_url: url,
    credit_startup: !!creditStartup,

    // optional fields:
    // eslint-disable-next-line @typescript-eslint/naming-convention
    ...(agreedToEmailPromo ? {Email_Opt_In__c: true} : {}),
    ...(numberOfEmployees ? {number_of_employees: numberOfEmployees} : {}),
    ...(promoSlug ? {promo_slug: promoSlug} : {}),
    ...(phone ? {phone} : {}),
    ...(next ? {next} : {}),
    ...(inviter ? {inviter} : {}),
    ...(incubator ? {incubator} : {}),
    ...(numberOfMaus ? {number_of_maus: numberOfMaus} : {}),
    ...(department ? {department} : {}),
    ...(extras ? {extras: JSON.stringify(extras)} : {}),
    ...(sscid ? {sscid} : {}),
  };

  try {
    const formData = objToFormData(userData);
    const rawResponse = await apiClient.post<string>(signupUrl, formData, {skipAccessToken: true});
    const response = JSON.parse(rawResponse); // TODO: @hilda update register view to not have json in json after retire depending htmls
    if (response.status_text === `success`) {
      return {
        statusText: `success`,
        redirectUrl: response.redirect_url,
      };
    } else {
      return {
        statusText: `error`,
        errors: JSON.parse(response.errors),
      };
    }
  } catch (err) {
    return {
      statusText: `error`,
      errors: {
        message: err.toString(),
        error: err,
      },
    };
  }
}
// auth-invitation-form tests verify that `avatar` param is being sent
// To do that, we need to stub the signup function, and sinon can't stub functions imported with destructuration
export const api = {signup};

export type SsoVerifyResponse = {
  isSsoEnabled: boolean;
  isSsoRequired: boolean;
};

export const verifySsoUrl = `/security/sso/verify/`;

export async function verifySsoGivenEmail(email: string): Promise<SsoVerifyResponse> {
  const response = await djangoFetchJson(verifySsoUrl, {}, {method: `GET`}, {email});
  return {
    isSsoEnabled: response.sso,
    isSsoRequired: response[`required_for_login`],
  };
}
